import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { Observable, of, from, EMPTY } from 'rxjs';
import { tap, switchMap, catchError, finalize } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { User } from '../models/user.model';

const MOCK_USER: User = {
  oid: '00000000-0000-0000-0000-000000000000',
  email: 'admin@wnt.local',
  display_name: 'Mock Admin',
  roles: JSON.parse(localStorage.getItem('mockRoles') ?? '["admin"]'),
  linked_employee_id: null,
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly msal = inject(MsalService, { optional: true });

  private readonly _user = signal<User | null>(null);
  private readonly _loading = signal(true);

  readonly user = this._user.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly isAuthenticated = computed(() => !!this._user());

  init(): Observable<void> {
    if (environment.authMock) {
      this._user.set(MOCK_USER);
      this._loading.set(false);
      return of(undefined);
    }

    return from(this.msal!.instance.initialize()).pipe(
      switchMap(() => from(this.msal!.instance.handleRedirectPromise())),
      tap((result) => {
        if (result?.account) {
          this.msal!.instance.setActiveAccount(result.account);
        }
      }),
      switchMap(() => {
        const accounts = this.msal!.instance.getAllAccounts();
        if (accounts.length === 0) {
          return from(
            this.msal!.instance.loginRedirect({
              scopes: environment.msalConfig.scopes,
            })
          ).pipe(switchMap(() => EMPTY));
        }
        this.msal!.instance.setActiveAccount(accounts[0]);
        return this.fetchMe();
      }),
      catchError((err) => {
        console.error('Auth init failed:', err);
        return of(undefined);
      }),
      finalize(() => this._loading.set(false)),
    );
  }

  private fetchMe(): Observable<void> {
    return this.http.get<User>('/api/auth/me').pipe(
      tap((user) => this._user.set(user)),
      switchMap(() => of(undefined)),
      catchError(() => {
        this._user.set(null);
        return of(undefined);
      }),
    );
  }

  hasRole(role: string): boolean {
    return this._user()?.roles.includes(role) ?? false;
  }

  hasAnyRole(...roles: string[]): boolean {
    const userRoles = this._user()?.roles ?? [];
    return roles.some((r) => userRoles.includes(r));
  }

  switchMockRole(roles: string[]): void {
    localStorage.setItem('mockRoles', JSON.stringify(roles));
    window.location.reload();
  }

  logout(): Observable<void> {
    if (environment.authMock) {
      this._user.set(null);
      return of(undefined);
    }
    return from(this.msal!.instance.logoutRedirect()).pipe(
      switchMap(() => of(undefined)),
    );
  }
}
